'use client';

import { useState, useRef, useEffect } from 'react';
import { Bot, Loader, Send, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { interactivePortfolioChat, type InteractivePortfolioChatInput } from '@/ai/flows/interactive-portfolio-chat';

type ConversationEntry = {
  role: 'user' | 'bot';
  content: string;
};

export default function ChatSection({ id }: { id: string }) {
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('div[data-radix-scroll-area-viewport]');
        if (viewport) {
            viewport.scrollTo({
              top: viewport.scrollHeight,
              behavior: 'smooth'
            });
        }
    }
  };

  useEffect(() => {
    scrollToBottom();
    if (!isLoading && isChatStarted) {
      inputRef.current?.focus();
    }
  }, [conversationHistory, isLoading]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsChatStarted(true);

    if (!userInput.trim() || isLoading) return;

    const userMessage = userInput;
    const newConversationHistory: ConversationEntry[] = [...conversationHistory, { role: 'user', content: userMessage }];
    setConversationHistory(newConversationHistory);
    setUserInput('');
    setIsLoading(true);

    try {
      const chatInput: InteractivePortfolioChatInput = {
        message: userMessage,
        conversationHistory: conversationHistory.map(entry => ({...entry})),
      };
      const result = await interactivePortfolioChat(chatInput);
      setConversationHistory(result.updatedConversationHistory.map(entry => ({...entry})));
    } catch (error) {
      console.error('Chat error:', error);
      // Removed the hardcoded error message. If there's an actual error, it will now show up.
      const errorMessage: ConversationEntry = { role: 'bot', content: 'An error occurred. Please try again.' };
      setConversationHistory((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id={id} className="py-24 bg-background sm:py-32">
      <div className="container px-4 mx-auto sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center lg:max-w-4xl">
          <h2 className="text-4xl font-bold tracking-tight font-headline text-primary sm:text-5xl">
            Want to know anything else?
          </h2>
          <p className="mt-4 text-lg text-foreground/80">Just ask! I've set up an AI to answer your questions.</p>
        </div>
        <div className="max-w-3xl mx-auto mt-16">
          <div className="p-4 border rounded-lg shadow-lg bg-card">
            <ScrollArea 
              className={cn(
                "w-full pr-4 transition-all duration-300",
                conversationHistory.length === 0 ? "h-[150px]" : "min-h-[200px] max-h-[600px]"
              )} 
              ref={scrollAreaRef}
              style={{
                height: conversationHistory.length > 0 
                  ? `${Math.min(600, Math.max(200, conversationHistory.length * 100 + 100))}px` 
                  : undefined
              }}
            >
              <div className="space-y-6">
                {conversationHistory.length === 0 && (
                  <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                    <div>
                      <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Start a conversation by asking me anything about Marcos!</p>
                    </div>
                  </div>
                )}
                {conversationHistory.map((entry, index) => (
                  <div key={index} className={cn('flex items-start gap-4', entry.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {entry.role === 'bot' && (
                      <Avatar className="w-8 h-8 border-2 border-primary">
                        <AvatarFallback><Bot className="w-4 h-4" /></AvatarFallback>
                      </Avatar>
                    )}
                    <div className={cn('max-w-xs md:max-w-md lg:max-w-lg px-4 py-3 rounded-2xl', entry.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-none' : 'bg-muted rounded-bl-none')}>
                      <p className="text-sm">{entry.content}</p>
                    </div>
                     {entry.role === 'user' && (
                      <Avatar className="w-8 h-8 border-2 border-accent">
                        <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
                {isLoading && (
                   <div className="flex items-start gap-4 justify-start">
                     <Avatar className="w-8 h-8 border-2 border-primary">
                       <AvatarFallback><Bot className="w-4 h-4" /></AvatarFallback>
                     </Avatar>
                     <div className="flex items-center justify-center max-w-lg px-4 py-3 bg-muted rounded-2xl rounded-bl-none">
                       <Loader className="w-5 h-5 animate-spin text-primary" />
                     </div>
                   </div>
                )}
              </div>
            </ScrollArea>
            <form onSubmit={handleSubmit} className="flex items-center gap-4 pt-4 mt-4 border-t">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Ask a question..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                disabled={isLoading}
                className="flex-1"
              />
              <Button type="submit" disabled={isLoading || !userInput.trim()} size="icon">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
